import { useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ExternalLink, Maximize2, X, ShieldAlert, Info } from "lucide-react";

interface FactData {
  id: string;
  text: string;
  conditions: string | null;
  confidence_score: number;
  difficulty_level: string | null;
  tags: string[];
  source_type: string;
  confirmation_count: number;
  source_id: string;
  source?: {
    title: string;
    url: string | null;
    authors: string | null;
    source_type: string;
  };
  occurrences_count?: number;
}

interface FactPopoverProps {
  factId: string;
  index: number;
  children: ReactNode;
}

export function FactPopover({ factId, index, children }: FactPopoverProps) {
  const [fact, setFact] = useState<FactData | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);

  const fetchFact = async () => {
    if (fact || notFound) return;
    setLoading(true);

    // Strip "FACT-" prefix if present
    const cleanId = factId.replace(/^FACT-/i, "");
    
    // For short IDs (8-char prefix), pad to create a UUID range filter
    // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    let query = supabase
      .from("facts")
      .select("id, text, conditions, confidence_score, difficulty_level, tags, source_type, confirmation_count, source_id")
      .eq("status", "validated");

    if (cleanId.length < 36) {
      // Use range: from "prefix-0000..." to "prefix-ffff..."
      const low = cleanId.padEnd(8, "0") + "-0000-0000-0000-000000000000";
      const high = cleanId.padEnd(8, "f") + "-ffff-ffff-ffff-ffffffffffff";
      query = query.gte("id", low).lte("id", high);
    } else {
      query = query.eq("id", cleanId);
    }

    const { data } = await query.maybeSingle();

    if (data) {
      const { data: source } = await supabase
        .from("sources")
        .select("title, url, authors, source_type, storage_path")
        .eq("id", data.source_id)
        .maybeSingle();

      // If source is a PDF with no URL but has storage_path, get signed URL
      let resolvedSource = source ? { ...source } : undefined;
      if (resolvedSource && !resolvedSource.url && resolvedSource.storage_path) {
        const { data: signedData } = await supabase.storage
          .from("source-documents")
          .createSignedUrl(resolvedSource.storage_path, 300);
        if (signedData?.signedUrl) {
          resolvedSource.url = signedData.signedUrl;
        }
      }

      const { count } = await supabase
        .from("fact_occurrences")
        .select("id", { count: "exact", head: true })
        .eq("fact_id", data.id);

      setFact({ ...data, source: resolvedSource ? { title: resolvedSource.title, url: resolvedSource.url, authors: resolvedSource.authors, source_type: resolvedSource.source_type } : undefined, occurrences_count: count || 0 });
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  const levelLabel = (level: string | null) => {
    const map: Record<string, string> = {
      beginner: "Débutant",
      intermediate: "Intermédiaire",
      advanced: "Avancé",
    };
    return level ? map[level] || level : null;
  };

  const truncate = (text: string, max: number) =>
    text.length > max ? text.slice(0, max) + "…" : text;

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchFact(); }}>
        <PopoverTrigger asChild>
          {children}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start" side="top">
          {loading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : fact ? (
            <div className="p-4 space-y-3">
              <p className="text-sm text-foreground leading-relaxed">
                {truncate(fact.text, 200)}
              </p>

              {fact.conditions && (
                <div className="rounded-md border border-yellow-500/20 bg-yellow-500/5 px-2.5 py-1.5 flex items-start gap-1.5">
                  <Info className="h-3 w-3 text-yellow-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-yellow-500">{fact.conditions}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-1.5">
                {levelLabel(fact.difficulty_level) && (
                  <Badge variant="outline" className="text-[10px]">
                    {levelLabel(fact.difficulty_level)}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                  Confiance {Math.round(fact.confidence_score * 100)}%
                </Badge>
                {fact.confirmation_count > 1 && (
                  <Badge variant="outline" className="text-[10px]">
                    {fact.confirmation_count} sources
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1 border-t border-border">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs flex-1"
                  onClick={() => { setOpen(false); setFullOpen(true); }}
                >
                  <Maximize2 className="h-3 w-3 mr-1" />
                  Ouvrir en grand
                </Button>
                {fact.source?.url && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs flex-1"
                    asChild
                  >
                    <a href={fact.source.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Voir la source
                    </a>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-10 w-10 p-0"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 text-xs text-muted-foreground">Fait non trouvé ou non validé.</div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-lg max-h-[80dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-display">Fiche du fait scientifique</DialogTitle>
          </DialogHeader>
          {fact ? (
            <div className="space-y-4">
              <p className="text-sm text-foreground leading-relaxed">{fact.text}</p>

              {fact.conditions && (
                <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-3 py-2 flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-yellow-500">Conditions d'application</p>
                    <p className="text-xs text-yellow-400 mt-0.5">{fact.conditions}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {levelLabel(fact.difficulty_level) && (
                  <Badge variant="outline" className="text-xs">
                    {levelLabel(fact.difficulty_level)}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                  Confiance {Math.round(fact.confidence_score * 100)}%
                </Badge>
                {fact.confirmation_count > 1 && (
                  <Badge variant="outline" className="text-xs">
                    {fact.confirmation_count} sources confirment
                  </Badge>
                )}
                {(fact.occurrences_count || 0) > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {fact.occurrences_count} occurrence(s)
                  </Badge>
                )}
                {fact.tags?.map((t) => (
                  <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                ))}
              </div>

              {fact.source && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                  <p className="text-xs font-medium text-foreground">{fact.source.title}</p>
                  {fact.source.authors && (
                    <p className="text-[11px] text-muted-foreground">{fact.source.authors}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground uppercase">{fact.source.source_type}</p>
                  {fact.source.url && (
                    <a
                      href={fact.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Accéder à la source
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Fait non disponible.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
